import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axiosInstance, { endpoints } from "../../../services/axios";
import { get, isString } from "lodash";
import { BsFolder, BsThreeDotsVertical } from "react-icons/bs";
import cx from "classnames";
import dayjs from "dayjs";
import {
  App,
  Button,
  Dropdown,
  Flex,
  Input,
  MenuProps,
  message,
  Space,
  Table,
  Tooltip,
  Typography,
} from "antd";
import { ISurvey } from "models";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  EditOutlined,
  FileAddOutlined,
  FolderAddOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import useNavigateWithParams from "hooks/use-navigate-with-params";
import { useBoolean } from "hooks/use-boolean";
import CreateFolderModal from "./CreateFolderModal";
import MoveFolderDialog from "./MoveFolderDialog";
import ScheduleModal from "./EmployeesModal";
import { useMemo, useState } from "react";

interface Props {
  permissions: Record<string, boolean>;
}

const SchedulesTab = ({ permissions }: Props) => {
  const navigate = useNavigateWithParams();
  const { modal } = App.useApp();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [sortType, setSortType] = useState<"down" | "up" | "">("");
  const token = searchParams.get("token");
  const folder = useBoolean();
  const employees = useBoolean();
  const moveFolderBool = useBoolean();

  const { data: schedulesData, isFetching } = useQuery({
    queryKey: ["schedules-list"],
    queryFn: async () => {
      axiosInstance.defaults.headers.common.Authorization = token;
      return await axiosInstance.get(endpoints.schedule.list);
    },
  });

  const confirmDelete = (id: string, isFolder: boolean) => () => {
    modal.confirm({
      title: "Delete Schedule",
      content: `Are you sure you want to delete this ${
        isFolder ? "folder" : "schedule"
      }`,

      onOk: async () =>
        await axiosInstance
          .delete(
            isFolder
              ? endpoints.folder.delete(id)
              : endpoints.schedule.delete(id),
          )
          .then(() => {
            queryClient.invalidateQueries({ queryKey: ["schedules-list"] });
            message.success("Successfully deleted!");
          })
          .catch(() => ""),

      okText: "Yes, delete",
      cancelText: "No, cancel",
    });
  };

  const { mutate: handleCreateFolder, isPending: isFolderCreating } =
    useMutation({
      mutationFn: async (data: any) => {
        if (isString(folder.data)) data.parentId = folder.data;

        if (get(folder, "data.id")) {
          return await axiosInstance.put(
            endpoints.folder.update(folder.data.id),
            data,
          );
        } else {
          return await axiosInstance.post(endpoints.folder.add, data);
        }
      },
      onSuccess: ({ data }) => {
        folder.onFalse();
        queryClient.invalidateQueries({ queryKey: ["schedules-list"] });
        message.success(
          data.id
            ? "Folder updated successfully!"
            : "Folder created successfully!",
        );
      },
    });

  const { mutate: handleMoveToFolder, isPending: isMoving } = useMutation({
    mutationFn: async (parentId: string) => {
      if (get(moveFolderBool, "data.folder")) {
        return await axiosInstance.put(
          endpoints.folder.update(get(moveFolderBool, "data.id")),
          { ...moveFolderBool.data, parentId },
        );
      }

      return await axiosInstance.put(
        endpoints.schedule.moveToFolder(
          parentId,
          get(moveFolderBool, "data.id"),
        ),
      );
    },
    onSuccess: ({ data }) => {
      moveFolderBool.onFalse();
      queryClient.invalidateQueries({ queryKey: ["schedules-list"] });
      message.success(data.id ? "Successfully moved!" : "Successfully moved!");
    },
  });

  const inputSearchHandler = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchTerm(e.currentTarget.value);
  };

  const usersList: any[] = useMemo(() => {
    let tempUsers = get(schedulesData, "data.data", []);

    if (sortType === "down") {
      tempUsers = [...tempUsers].sort(
        (a, b) => (b.employeesCount || 0) - (a.employeesCount || 0),
      );
    } else if (sortType === "up") {
      tempUsers = [...tempUsers].sort(
        (a, b) => (a.employeesCount || 0) - (b.employeesCount || 0),
      );
    }

    return tempUsers.filter(({ name }: any) =>
      [name].some((n) => n.toLowerCase().includes(searchTerm.toLowerCase())),
    );
  }, [schedulesData, searchTerm, sortType]);

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      ellipsis: {
        showTitle: false,
      },
      render: (value: any, row: any) =>
        !row.folder ? (
          <Tooltip placement="topLeft" title={value}>
            <Link to={`/schedule/${row.id}?token=${token}`}>{value}</Link>
          </Tooltip>
        ) : (
          <Flex gap={10} align="center">
            <BsFolder />
            <Typography.Text>{value}</Typography.Text>
          </Flex>
        ),
    },
    {
      title: (
        <Flex
          className="*:m-0 cursor-pointer"
          justify="space-between"
          align="center"
          onClick={() =>
            setSortType(
              sortType === "down"
                ? "up"
                : sortType === "up"
                  ? ""
                  : sortType === ""
                    ? "down"
                    : "",
            )
          }
        >
          <p>Employees</p>
          <Flex gap={2}>
            <ArrowDownOutlined
              className={cx(
                sortType === "down" ? "text-primary" : "text-gray-300",
              )}
            />
            <ArrowUpOutlined
              className={cx(
                sortType === "up" ? "text-primary" : "text-gray-300",
              )}
            />
          </Flex>
        </Flex>
      ),
      dataIndex: "employeesCount",
      width: 180,
      render: (value: any, row: any) => (
        <center>
          <p
            onClick={() => employees.onTrue(row.id)}
            className="px-2 w-max rounded-md transition-colors cursor-pointer hover:bg-primary hover:text-white "
          >
            {value}
          </p>
        </center>
      ),
    },

    {
      title: <center>Created at</center>,
      dataIndex: "createdAt",
      width: 180,
      render: (value: any) => (
        <center>{dayjs(value).format("DD MMM YYYY HH:mm")}</center>
      ),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "id",
      align: "right" as "right",
      width: 150,
      render: (_: any, row: ISurvey) => {
        return row.folder && permissions?.canAddSchedule ? (
          <Space>
            <Button
              onClick={() => folder.onTrue(row.id)}
              icon={<FolderAddOutlined />}
            />
            <Button
              onClick={() => navigate("/schedule", { folderId: row.id })}
              type="primary"
              icon={<FileAddOutlined />}
            />
          </Space>
        ) : null;
      },
    },
    {
      title: "",
      dataIndex: "dots",
      key: "id",
      align: "right" as "right",
      width: 50,
      render: (_: any, row: ISurvey) => {
        const items: MenuProps["items"] = [];

        if (permissions?.canAddSchedule)
          items.push({
            key: "move",
            label: "Move to folder",
            icon: <FolderAddOutlined />,
            onClick: () => moveFolderBool.onTrue(row),
            //
          });
        if (permissions?.canAddSchedule)
          items.push({
            key: "edit",
            label: "Edit",
            icon: <EditOutlined />,
            onClick: () => {
              if (row.folder) folder.onTrue(row);
              else navigate(`/schedule/${row.id}`);
            },
            //
          });
        if (permissions?.canDeleteSchedule)
          items.push({
            key: "delete",
            label: "Delete",
            onClick: confirmDelete(row.id, !!row.folder),
            icon: <DeleteOutlined />,
            danger: true,
          });

        return (
          <Dropdown menu={{ items }} placement="bottomRight">
            <Button size="small" type="text" icon={<BsThreeDotsVertical />} />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div>
      <Flex align="center" justify="space-between" className="my-3">
        <Typography.Title className="mt-0" level={4}>
          Schedules
        </Typography.Title>

        {get(permissions, "canAddSchedule") && (
          <Space>
            <Flex>
              <Input
                placeholder="Search"
                prefix={<SearchOutlined />}
                onChange={inputSearchHandler}
              />
            </Flex>
            <Button variant="outlined" onClick={() => folder.onTrue()}>
              + Add folder
            </Button>
            <Button type="primary" onClick={() => navigate("/schedule")}>
              + Add new
            </Button>
          </Space>
        )}
      </Flex>

      <Table
        loading={isFetching}
        rowKey={"id"}
        expandable={{
          rowExpandable: (row: any) => get(row, "children.length"),
        }}
        pagination={false}
        dataSource={usersList}
        columns={columns}
      />

      <MoveFolderDialog
        loading={isMoving}
        onCancel={moveFolderBool.onFalse}
        schedules={usersList}
        moveToFolder={handleMoveToFolder}
        dialogBool={moveFolderBool}
      />

      <CreateFolderModal
        folderName={get(folder, "data.id") ? folder.data.name : null}
        submitLoading={isFolderCreating}
        isOpen={folder.value}
        onCancel={folder.onFalse}
        onSubmit={handleCreateFolder}
        //
      />
      <ScheduleModal
        id={get(employees, "data") ? employees.data : null}
        onClose={employees.onFalse}
        visible={employees.value}
      />
    </div>
  );
};

export default SchedulesTab;
